import React, { ClipboardEvent, createRef, useEffect, useState } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';

import Spinner from 'src/components/Spinner';
import { LoginType } from 'src/services/GraphQL/Queries/types';
import {
  CAPTCHA_CHECK_BOX_MESSAGE,
  KEY_CODES,
  LOGIN_REGISTER_DEFAULTS
} from 'src/utils/constants/utilConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import { OTP_VALIDATION_TYPE } from '../../../utils/getOTPValidationTypes';
import ToastMessage from '../../toastMessage';
import * as style from './EnterOtpCss';

const DIGIT_REGEX = /^[0-9]$/;
const TIMER_DURATION = 59;
const NUMBER_OF_INPUTS = 4;

type OptProps = {
  inputType: string;
  inputValue: string;
  show: boolean;
  otpErrorMessage?: string;
  hideEditOption?: boolean;
  onClose?: () => void;
  handleVerifyOtp: (otp: string, inputType: string, inputValue: string) => void;
  resendOTP: (
    inputType: string,
    inputValue: string,
    captchaValue?: string | null
  ) => void;
  handleEditInput: () => void;
  OTPValidationFor?: string;
  setOtpErrorMessage?: (message: string) => void;
  countryCodeData?: CountryCodeDataType[];
  userType?: number;
};
type CountryCodeDataType = {
  validMobileDigits: string;
  validMobileDigitMax: string;
  value: string;
  phoneCode: string;
  mobileRegistration: string;
  mobileRegistrationWithOtp: string;
  label: string;
};

const EnterOtp = ({
  inputType,
  inputValue,
  show,
  otpErrorMessage,
  hideEditOption,
  handleVerifyOtp,
  setOtpErrorMessage,
  resendOTP,
  handleEditInput,
  OTPValidationFor = OTP_VALIDATION_TYPE.userLogin,
  countryCodeData,
  userType
}: OptProps) => {
  const { translations: allTranslations, usersLocationData } = useAppStore();

  const LOGIN_REGISTER_STRINGS = allTranslations?.loginRegister;

  //Timer duration
  const [seconds, setSeconds] = useState(TIMER_DURATION);

  // Spinner
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  //create array of refs
  const [inputRefsArray] = useState(() => {
    return Array.from({ length: NUMBER_OF_INPUTS }, () =>
      createRef<HTMLInputElement>()
    );
  });

  //state for current index value
  const [currentIndex, setCurrentIndex] = useState(0);

  //save the otp digits
  const [digits, setDigits] = useState(() => {
    return Array.from({ length: NUMBER_OF_INPUTS }, () => '');
  });
  const currentCountry = usersLocationData?.country_code || 'IN';
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [captchaValidation, setCaptchaValidation] = useState<boolean>(false);
  const [registerWithMobile, setRegisterWithMobile] = useState(false);
  const [mobileRegistrationWithOtp, setmobileRegistrationWithOtp] = useState<
    string | boolean
  >(true);
  const isEmail = inputValue?.match(LOGIN_REGISTER_DEFAULTS.emailRegex);

  /**
   * Checks all the digits of the otp are valid
   * @returns {boolean} true if all the digits are valid
   *
   */
  const validOtp = () => {
    let isValid = true;
    digits.forEach((digit) => {
      if (!digit.match(DIGIT_REGEX)) return (isValid = false);
    });
    return isValid;
  };
  const isValidOtp = validOtp();

  /**
   * Combines the OTP digits
   * @return {string} OTP string
   */
  const getOTPString = () => {
    let otpString = '';
    digits.forEach((digit) => {
      otpString = `${otpString}${digit}`;
    });
    return otpString;
  };

  // Handles the timer countdown
  useEffect(() => {
    if (!show) return setSeconds(TIMER_DURATION);
    setTimeout(() => {
      if (seconds === 0) {
        setCaptchaValidation(false);
        return;
      }
      setSeconds((ps) => ps - 1);
    }, 1000);
  }, [seconds, show]);

  //select the correct position of input
  useEffect(() => {
    if (currentIndex < NUMBER_OF_INPUTS && !otpErrorMessage) {
      inputRefsArray[currentIndex].current?.focus();
      inputRefsArray[currentIndex].current?.select();
    }
  }, [currentIndex, otpErrorMessage]);

  /**
   * This function handle the digit input and backspace event
   * @param {React.KeyboardEvent<HTMLInputElement>} event - Keyboard event
   * @param {number} index - index of selected input
   * @returns {void}
   */

  const handleOnWheel = (event: React.MouseEvent<HTMLInputElement>) => {
    event.preventDefault();
    // Prevent the input value change
    (event.target as HTMLElement).blur();
  };

  const handleKeyChange = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    ref: React.RefObject<HTMLInputElement>
  ) => {
    event.preventDefault();
    if (event.key.match(DIGIT_REGEX)) {
      const value = event.key;
      const cloneDigits = [...digits];
      cloneDigits[index] = value;
      setDigits(() => cloneDigits);
      if (index < NUMBER_OF_INPUTS - 1) {
        setCurrentIndex((previous) => previous + 1);
      }
    } else if (event.key === 'Backspace') {
      const cloneDigits = [...digits];
      cloneDigits[index] = '';
      setDigits(() => cloneDigits);
      if (index > 0) {
        setCurrentIndex((previous) => previous - 1);
      }
    } else if (event.key === KEY_CODES.ENTER) {
      if (validOtp()) handleVerifyOtpSubmit();
    } else if (
      ref.current !== null &&
      event.key !== KEY_CODES.ARROWUP &&
      event.key !== KEY_CODES.ARROWDOWN
    ) {
      ref.current.value = '';
    }
  };

  const handleOnChange = (e: any, index: number) => {
    const value = e.target.value;
    const regexForNumber = /^[0-9\b]+$/;
    const getLength = value.length === 2 ? 0 : value.length;
    if (getLength === 0 || regexForNumber.test(value)) {
      const cloneDigits = [...digits];
      cloneDigits[index] = value.substring(0, 1);
      setDigits(() => cloneDigits);
      if (index < NUMBER_OF_INPUTS - 1) {
        setCurrentIndex(index + 1);
      }
    }
  };

  /**
   * This function selects and focus on clicked input
   * @param {React.MouseEvent<HTMLInputElement, MouseEvent>} event - Mouse event
   * @param {number} index - index of selected input
   * @returns {void}
   */
  const handleClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>,
    index: number
  ) => {
    setCurrentIndex(index);
    const selectedInput = event.target as HTMLInputElement;
    selectedInput.focus();
    selectedInput.select();
  };

  function handleOnPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text/plain');
    if (currentIndex == 0) {
      setDigits(pasted.split('').slice(0, 4));
    } else {
      const copiedDigits = Array.from(
        { length: NUMBER_OF_INPUTS },
        () => ''
      ).map((digit, index) => {
        return index >= currentIndex
          ? pasted[index - currentIndex]
          : digits[index];
      });
      setDigits(copiedDigits);
    }
  }

  const handleVerifyOtpSubmit = () => {
    setLoadingSpinner(true);
    handleVerifyOtp(getOTPString(), inputType, inputValue);
  };

  const resendOTPClick = () => {
    if (
      (userType !== 0 && currentCountry !== 'IN' && captchaValue) ||
      currentCountry === 'IN' ||
      mobileRegistrationWithOtp ||
      userType === 0 ||
      isEmail
    ) {
      resendOTP(inputType, inputValue, captchaValue);
      setCaptchaValidation(false);
      setCaptchaValue(null);
      setSeconds(TIMER_DURATION);
    } else {
      setCaptchaValidation(true);
    }
  };

  const preventEventHandler = (e: any) => {
    e.preventDefault();
  };

  useEffect(() => {
    setLoadingSpinner(false);
    if (otpErrorMessage) {
      setTimeout(() => {
        setOtpErrorMessage && setOtpErrorMessage('');
      }, 2000);
    }
  }, [otpErrorMessage]);

  const mapModalDataWithTypes = {
    userLogin: {
      classes: {
        popupContainer: style.popUpContainer,
        popupBlock: style.popUpBlock,
        title: style.title,
        mobileInfo: style.mobileInfo,
        editDetailsWrapper: style.editDetailsWrapper,
        dottedBorder: style.dottedBottomBorder,
        editIcon: style.editIcon,
        inputContainer: style.otpInputContainer,
        inputField: style.otpInputField,
        verfyBtn: (validOtp: boolean) => style.verifyButton(validOtp),
        resendOtpWrapper: style.resendOtpWrapper,
        resendOtpInSec: style.resendOtpSeconds
      },
      translations: {
        Guest_Checkout_VerifyOTP_Text:
          LOGIN_REGISTER_STRINGS?.Guest_Checkout_VerifyOTP_Text
      }
    },
    userOnboard: {
      classes: {
        popupContainer: style.userOnboardPopUpContainer,
        popupBlock: style.userOnboardPopUpBlock,
        title: style.userOnboardTitle,
        mobileInfo: style.userOnboardMobileInfo,
        editDetailsWrapper: style.editDetailsWrapper,
        dottedBorder: style.userOnboardMobileDottedBorder,
        editIcon: style.editIcon,
        inputContainer: style.userOnboardOtpInputContainer,
        inputField: style.userOnboardOtpInputField,
        verfyBtn: (validOtp: boolean) =>
          style.userOnboardVerifyButton(validOtp),
        resendOtpWrapper: style.userOnboardResendOtpWrapper,
        resendOtpInSec: style.userOnboardResendOtpSeconds
      },
      translations: {
        Guest_Checkout_VerifyOTP_Text:
          LOGIN_REGISTER_STRINGS?.Guest_Checkout_VerifyOTP_Text_For_User_Onboard
      }
    }
  };
  const { classes: styles, translations } =
    mapModalDataWithTypes[
      OTPValidationFor as keyof typeof mapModalDataWithTypes
    ];
  useEffect(() => {
    if (countryCodeData && countryCodeData.length > 0 && currentCountry) {
      const countriesWithMobileRegistration = countryCodeData.filter(
        (data: CountryCodeDataType) => {
          return data?.mobileRegistration === 'true';
        }
      );
      if (
        countriesWithMobileRegistration.find((data: CountryCodeDataType) => {
          return data?.value === currentCountry;
        })
      ) {
        const getCountryInfo = countriesWithMobileRegistration.filter(
          (data: CountryCodeDataType) => {
            return data?.value === currentCountry;
          }
        );
        setRegisterWithMobile(true);
        if (getCountryInfo && getCountryInfo.length > 0) {
          setmobileRegistrationWithOtp(
            getCountryInfo[0]?.mobileRegistrationWithOtp
          );
        }
      }
    }
  }, [countryCodeData, currentCountry]);
  const handleChangeCaptcha = (value: string | null) => {
    setCaptchaValue(value);
  };

  return (
    <>
      <div css={styles.popupContainer} data-test-id="popUpContainer">
        {loadingSpinner && <Spinner />}
        <div css={styles.popupBlock} data-test-id="popUpBlock">
          <div css={styles.title}>
            {translations.Guest_Checkout_VerifyOTP_Text}
          </div>
          <div data-test-id="mobileInfo" css={styles.mobileInfo}>
            <span>
              {
                LOGIN_REGISTER_STRINGS?.Guest_Checkout_EnterOTP_Text.split(
                  '{{mobile_number}}'
                )[0]
              }
            </span>
          </div>
          <div data-test-id="editDetailsWrapper" css={style.editDetailsWrapper}>
            <span css={style.dottedBottomBorder}>
              {(inputType === LoginType.Mobile || inputType === 'phoneno') &&
                '+'}
              {inputValue}
            </span>
            {!hideEditOption ? (
              <span css={style.editIconWrapper} onClick={handleEditInput}>
                <img
                  css={style.editIcon}
                  src="/webapp-assets/images/edit.svg"
                  alt="edit"
                />
              </span>
            ) : null}
          </div>
          <div css={styles.inputContainer} data-test-id="otpInputContainer">
            {inputRefsArray.map((ref, index) => {
              return (
                <input
                  ref={ref}
                  key={index}
                  css={styles.inputField}
                  type="number"
                  maxLength={1}
                  onKeyUp={(event) => handleKeyChange(event, index, ref)}
                  onChange={(e) => handleOnChange(e, index)}
                  onKeyDown={preventEventHandler}
                  onClick={(event) => handleClick(event, index)}
                  onPaste={handleOnPaste}
                  value={digits[index]}
                  pattern="^[0-9]$"
                  onWheel={handleOnWheel}
                  inputMode="numeric"
                  data-testid={`input-box-${index + 1}`}
                />
              );
            })}
          </div>
          {otpErrorMessage &&
            OTPValidationFor == OTP_VALIDATION_TYPE.userLogin && (
              <ToastMessage message={otpErrorMessage} />
            )}
          {otpErrorMessage &&
            OTPValidationFor == OTP_VALIDATION_TYPE.userOnboard && (
              <div data-test-id="message" css={[style.message, style.error]}>
                <span>{otpErrorMessage}</span>
              </div>
            )}
          <button
            data-test-id="verifyButton"
            onClick={handleVerifyOtpSubmit}
            css={style.verifyButton(validOtp())}
            disabled={!isValidOtp}
            data-testid={`${isValidOtp ? '' : 'disable-'}vefiry-btn`}>
            {LOGIN_REGISTER_STRINGS?.CTA_Verify}
          </button>
          <div css={styles.resendOtpWrapper} data-test-id="resendOtpWrapper">
            {seconds > 0 ? (
              <span css={styles.resendOtpInSec}>
                {LOGIN_REGISTER_STRINGS?.Resend_OTP_In_seconds.replace(
                  '{{countdown}}',
                  seconds.toString()
                )}
              </span>
            ) : (
              <>
                {captchaValidation && (
                  <div css={style.captchaErrorMessage}>
                    {CAPTCHA_CHECK_BOX_MESSAGE}
                  </div>
                )}
                {userType !== 0 &&
                  mobileRegistrationWithOtp === false &&
                  registerWithMobile === true &&
                  !isEmail && (
                    <div css={style.reCaptchaContainer}>
                      <ReCAPTCHA
                        sitekey={
                          process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY as string
                        }
                        onChange={handleChangeCaptcha}
                      />
                    </div>
                  )}
                <span
                  css={style.resendOtpButton}
                  onClick={resendOTPClick}
                  data-testid="resend-otp-btn">
                  {LOGIN_REGISTER_STRINGS?.Guest_Checkout_ResendOTP1_Text}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EnterOtp;
